// components
import {
  ClickAwayListener,
  Divider,
  Paper,
  Popper,
  Tooltip
} from '@mui/material';
import { IconButton } from '@sdb/planning-ui';
import { PulsingBadge } from 'components/shared/Badge/PulsingBadge';

// styles
import {
  StyledBoxFlexColumnSmallGapCenter,
  StyledRelativeBox
} from 'components/shared/Box/Box.styles';
import { StyledPaperBox } from './VerticalToolbarActionWrapper.styles';

// utils
import { getToolbarButtonName } from '../ToolbarSet.utils';

// hooks
import { MouseEvent, useState } from 'react';

// types
import type { PopperPlacementType, TooltipProps } from '@mui/material';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type { BadgeColors } from 'components/shared/Badge/PulsingBadge.types';

const VerticalToolbarActionWrapper = ({
  children,
  iconType,
  title,
  divider,
  placement = 'left',
  tooltipPlacement = 'left',
  badge
}: {
  children: React.ReactNode;
  iconType: IconType;
  title: string;
  divider?: {
    top?: boolean;
    bottom?: boolean;
  };
  /** Where the popper opens relative to the icon. Defaults to 'left' for a side-docked toolbar. */
  placement?: PopperPlacementType;
  /** Where the tooltip sits relative to the icon. Defaults to 'left', matching the popper. */
  tooltipPlacement?: TooltipProps['placement'];
  /**
   * An optional count badge on the icon, the same PulsingBadge the flex-match toolbar uses for its
   * active-filter count. A number shows in the dot; leave content undefined for a plain bullet.
   */
  badge?: { isActive: boolean; content?: number; color?: BadgeColors };
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = !!anchorEl;
  const id = open ? 'settings-popper' : undefined;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  return (
    <ClickAwayListener mouseEvent="onMouseUp" onClickAway={handleClickAway}>
      <StyledBoxFlexColumnSmallGapCenter>
        {divider?.top && (
          <Divider orientation="horizontal" variant="middle" flexItem />
        )}
        <Tooltip title={title} placement={tooltipPlacement}>
          {/* The tooltip labels its direct child, this wrapper, not the button: drop that label and
              name the button itself. */}
          <StyledRelativeBox aria-label={undefined}>
            <IconButton
              type={iconType}
              size="small"
              onClick={handleClick}
              aria-label={getToolbarButtonName(title, badge)}
            />
            {badge?.isActive && (
              <PulsingBadge
                color={badge.color ?? 'success'}
                content={badge.content ?? ''}
              />
            )}
          </StyledRelativeBox>
        </Tooltip>
        <Popper
          placement={placement}
          anchorEl={anchorEl}
          open={open}
          id={id}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8]
              }
            }
          ]}
        >
          <Paper>
            <StyledPaperBox>{children}</StyledPaperBox>
          </Paper>
        </Popper>
        {divider?.bottom && (
          <Divider orientation="horizontal" variant="middle" flexItem />
        )}
      </StyledBoxFlexColumnSmallGapCenter>
    </ClickAwayListener>
  );
};

export default VerticalToolbarActionWrapper;
